import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { BADGE_COLORS } from "./Badge";

/**
 * Solid Tailwind swatch class for each color we accept. These match the
 * families used in <Badge> but render as fully-saturated dots so the
 * picker reads as colour-first instead of text-first.
 */
const SWATCH_BG: Record<string, string> = {
  zinc: "bg-zinc-400",
  indigo: "bg-indigo-500",
  blue: "bg-blue-500",
  // `sky` and `teal` are used throughout the seeded menu, seats and
  // categories. Without entries here they fell back to grey, so a sky-blue
  // treatment chair drew a black dot.
  sky: "bg-sky-500",
  teal: "bg-teal-500",
  cyan: "bg-cyan-500",
  emerald: "bg-emerald-500",
  green: "bg-green-500",
  amber: "bg-amber-500",
  yellow: "bg-yellow-500",
  orange: "bg-orange-500",
  rose: "bg-rose-500",
  red: "bg-red-500",
  pink: "bg-pink-500",
  violet: "bg-violet-500",
  purple: "bg-purple-500",
  fuchsia: "bg-fuchsia-500",
  slate: "bg-slate-500",
};

export function colorSwatchClass(color: string): string {
  return SWATCH_BG[color] ?? SWATCH_BG.zinc!;
}

/**
 * Compact visual swatch picker. Shows the current colour as a circle;
 * clicking opens a popover grid of every available colour. Replaces text
 * dropdowns that listed colour names — picking a colour you can't see is
 * a UX bug.
 */
export function ColorSwatchPicker({
  value, onChange, className,
}: {
  value: string;
  onChange: (c: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`Color: ${value}`}
        aria-label={`Color: ${value}`}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900/50 hover:border-zinc-600 transition-colors"
      >
        <span className={cn("h-4 w-4 rounded-full ring-1 ring-white/10", colorSwatchClass(value))} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded-xl border border-zinc-800 bg-zinc-950 p-2 shadow-xl shadow-black/40 animate-fade-in">
          <div className="grid grid-cols-5 gap-1.5">
            {BADGE_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setOpen(false); }}
                title={c}
                aria-label={c}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full ring-1 transition-transform hover:scale-110",
                  colorSwatchClass(c),
                  c === value ? "ring-white/80 ring-offset-1 ring-offset-zinc-950" : "ring-white/10",
                )}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
